import { memo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  AuraLayout,
  Button,
  ElevatedCircle,
  GradientText,
  Layout,
  PageTitle,
} from "lola-framework-ui-test";
import { ErrorIcon } from "lola-framework-ui-test/src/icons";
import { useStore } from "../../../store/globalState";
import {
  errorInitialState,
  flavorClassName,
} from "../../../constants";
import type { IErrorState } from "../interfaces/error";

export const ErrorPage = memo(() => {
  const { t } = useTranslation();
  const { theme, errorState } = useStore();
  const location = useLocation();
  const navigate = useNavigate();

  // Use whiteView like LandingPage for consistent styling
  const colorConfig = theme.errorView;
  const {
    backgroundBtn,
    textColorBtn,
    dropzoneColors,
    title,
    bodyCopy,
    buttonShowIcon,
    buttonSize,
  } = colorConfig;

  // Get error from location.state first, then errorState from store, then default
  const error =
    (location.state as IErrorState) || errorState || errorInitialState;
  const {
    title: errorTitle,
    subTitle = "",
    message,
    btnText,
    isOut,
    pageUrl,
  } = error;

  const handleRetry = () => {
    const isExternalUrl =
      typeof pageUrl === "string" &&
      (pageUrl.startsWith("http://") || pageUrl.startsWith("https://"));

    if ((isOut || isExternalUrl) && pageUrl) {
      window.location.href = `${pageUrl}`;
      return;
    }

    const newPageUrl = pageUrl || -1;
    navigate(newPageUrl as unknown as string);
  };

  return (
    <AuraLayout colorConfig={colorConfig} className={`${flavorClassName}`}>
      <Layout.Content className="landing-section error-page">
        <ElevatedCircle
          shadowVariant="inset"
        >
          <ErrorIcon size={60} colors={dropzoneColors} />
        </ElevatedCircle>
        <section className="landing-container">
          <PageTitle
            highlight={errorTitle || t("error.title")}
            highlightColor={title}
            secudnary={subTitle}
            secudnaryColor={title}
          />
          <GradientText as="p" textColor={bodyCopy} className="text-centered">
            {message || t("error.tryAgainLater")}
          </GradientText>
        </section>
      </Layout.Content>
      <Layout.Footer>
        <section className="footer-section">
          {/* Only show button if there's a target to navigate to or explicit button text */}
          {(btnText || pageUrl) && (
            <Button
              size={buttonSize ?? "small"}
              showIcon={buttonShowIcon}
              background={backgroundBtn}
              color={textColorBtn}
              onClick={handleRetry}
            >
              {btnText || t("common.retry")}
            </Button>
          )}
        </section>
      </Layout.Footer>
    </AuraLayout>
  );
});
